import { z } from 'zod'

/**
 * Schema for the Dun & Bradstreet number import. Two columns: the Customer ERP and the D&B number.
 *
 * The upload worker parses the file positionally against `importDnbHeaders`, so that array is both
 * the downloadable template header row and the object keys handed to this schema. The D&B value has
 * its dashes stripped and must be exactly 9 digits afterwards; anything else fails schema parsing and
 * the row is reported invalid. The Customer ERP has its leading zeros stripped to match the
 * canonicalized `customers.customer_number`; a blank ERP becomes null and is caught downstream.
 */

export const importDnbHeaders = ['Customer ERP', 'D&B Number'] as const

/** Customer ERP: strip leading zeros to match the canonicalized customer number; blank -> null. */
const dnbCustomerErpSchema = z
  .string()
  .nullable()
  .default(null)
  .transform((value) => {
    const trimmed = (value ?? '').trim().replace(/^0+/, '')
    return trimmed === '' ? null : trimmed
  })

/** D&B (DUNS) number: strip every dash, then require exactly 9 digits. */
const dnbNumberSchema = z
  .string()
  .nullable()
  .default(null)
  .transform((value) => (value ?? '').replace(/-/g, '').trim())
  .refine((value) => /^\d{9}$/.test(value), {
    message: 'D&B number must be exactly 9 digits after removing dashes',
  })

export const importDnbSchema = z.object({
  'Customer ERP': dnbCustomerErpSchema,
  'D&B Number': dnbNumberSchema,
})

export type ImportDnbRecord = z.infer<typeof importDnbSchema>
